import {
  InspectionProperty,
  InspectionTypeEnum,
  InspectionTypes
} from '@authority-labs/enums';
import { isEmpty } from 'lodash';
import { BaseDto } from '../base.dto';
import { Order } from '../order.dto';
import { InspectionAirConditioningAndVentilationSystems } from './air-conditioning-and-ventilation-systems';
import { Disclaimer } from './disclaimer.dto';
import { InspectionFireAlarmSystem } from './fire-alarm-system/inspection-fire-alarm-system.dto';
import { InspectionFireExtinguishers } from './fire-extinguishers/inspection-fire-extinguishers.dto';
import { InspectionFireTaps } from './fire-taps/inspection-fire-taps.dto';
import { InspectionGasExtinguishingSystem } from './gas-extinguishing-system/inspection-gas-extinguishing-system.dto';
import { InspectionAerosolShutdown } from './inspection-aerosol-shutdown';
import { SummaryNotes } from './inspection-cases/inspection-case.dto';
import { InspectionDesign } from './inspection-design/inspection-design.dto';
import { InspectionInstallation } from './inspection-installation/inspection-installation.dto';
import { InspectionIntegration } from './integration/inspection-integration.dto';
import { InspectionPressureSystemInStairwellsCompatibilityCheck } from './pressure-system-in-stairwells-compatibility-check';
import { InspectionVentilationSystemAndFireProtection } from './ventilation-system-and-fire-protection-in-commercial-cooking-systems';
import { WaterFlowPressure } from './water-flow-pressure.dto';
import { WaterNetworkCharacterization } from './water-network-characterization.dto';

export interface Cases {
  title: string;
  cases: Inspection[];
}

export interface Inspection {
  title?: string;
  isPassed?: boolean;
  isApplicable?: boolean;
  setInspectionStatus?(): void;
  removeNonApplicable?(): void;
  setAsApplicable?(): void;
  getCases?(): Cases[];
}

export class InspectionDto extends BaseDto {
  orderId?: number;
  version?: number;
  isPassed?: boolean;
  standard?: string;
  standardExtraText?: string;
  inspectionType?: InspectionTypeEnum;
  fireExtinguishingType?: string;
  inspectionDate?: Date;
  summaryNotes?: SummaryNotes[];
  disclaimer?: Disclaimer;
  order?: Partial<Order>;
  inspectionCount?: string;
  representativeName?: string;
  inspectionDesign?: InspectionDesign;
  inspectionInstallation?: InspectionInstallation;
  inspectionWaterNetworkCharacterization?: WaterNetworkCharacterization;
  inspectionGasExtinguishingSystem?: InspectionGasExtinguishingSystem;
  inspectionFireTaps?: InspectionFireTaps;
  inspectionFireExtinguishers?: InspectionFireExtinguishers;
  inspectionAerosolShutdown?: InspectionAerosolShutdown;
  inspectionFireAlarmSystem?: InspectionFireAlarmSystem;
  inspectionAirConditioningAndVentilationSystems?: InspectionAirConditioningAndVentilationSystems;
  inspectionVentilationSystemAndFireProtection?: InspectionVentilationSystemAndFireProtection;
  inspectionPressureSystemInStairwellsCompatibilityCheck?: InspectionPressureSystemInStairwellsCompatibilityCheck;
  inspectionIntegration?: InspectionIntegration;
}

export class InspectionModel extends InspectionDto {
  /**
   * Returns initialized section data for a given inspection type.
   * Used by the backend to pre-populate sections when creating an inspection.
   */
  static initializeSections(type: InspectionTypeEnum): Partial<InspectionDto> {
    const sections: Partial<InspectionDto> = {
      disclaimer: {
        isThereConflictOfInterest: false,
        isThereReasonForBias: false
      },
      summaryNotes: []
    };

    switch (type) {
      case InspectionTypeEnum.InspectionDesign:
        sections.inspectionDesign = new InspectionDesign();
        break;
      case InspectionTypeEnum.InspectionInstallation:
      case InspectionTypeEnum.InspectionDesignAndInstallation:
        sections.inspectionDesign = new InspectionDesign();
        sections.inspectionInstallation = new InspectionInstallation();
        break;
      case InspectionTypeEnum.InspectionWaterNetworkCharacterization:
        sections.inspectionWaterNetworkCharacterization =
          new WaterNetworkCharacterization({
            isApplicable: true,
            summaryNotes: []
          });
        break;
      case InspectionTypeEnum.InspectionGasExtinguishingSystem:
        sections.inspectionGasExtinguishingSystem =
          new InspectionGasExtinguishingSystem();
        break;
      case InspectionTypeEnum.InspectionFireTaps:
        sections.inspectionFireTaps = new InspectionFireTaps();
        break;
      case InspectionTypeEnum.InspectionFireExtinguishers:
        sections.inspectionFireExtinguishers =
          new InspectionFireExtinguishers();
        break;
      case InspectionTypeEnum.InspectionAerosolShutdown:
        sections.inspectionAerosolShutdown = new InspectionAerosolShutdown();
        break;
      case InspectionTypeEnum.InspectionFireAlarmSystem:
        sections.inspectionFireAlarmSystem = new InspectionFireAlarmSystem();
        break;
      case InspectionTypeEnum.InspectionAirConditioningAndVentilationSystems:
        sections.inspectionAirConditioningAndVentilationSystems =
          new InspectionAirConditioningAndVentilationSystems();
        break;
      case InspectionTypeEnum.InspectionVentilationSystemAndFireProtection:
        sections.inspectionVentilationSystemAndFireProtection =
          new InspectionVentilationSystemAndFireProtection();
        break;
      case InspectionTypeEnum.InspectionPressureSystemInStairwellsCompatibilityCheck:
        sections.inspectionPressureSystemInStairwellsCompatibilityCheck =
          new InspectionPressureSystemInStairwellsCompatibilityCheck();
        break;
      case InspectionTypeEnum.InspectionIntegration:
        sections.inspectionIntegration = new InspectionIntegration();
        break;
    }

    return sections;
  }

  get inspections(): Inspection[] {
    return InspectionTypes.map((key: InspectionProperty) => this[key]).filter(
      (x) => !!x
    );
  }

  get isSignedDisclaimer(): boolean {
    return (
      this?.disclaimer?.isThereConflictOfInterest !== undefined &&
      this?.disclaimer?.isThereReasonForBias !== undefined
    );
  }

  get isKnownInspectionType(): boolean {
    const knownInspectionType = Object.keys(InspectionTypeEnum).includes(
      this.inspectionType
    );

    return knownInspectionType;
  }

  get waterFlowPressure(): WaterFlowPressure {
    if (
      this?.inspectionDesign?.sprinklerSystemDesign?.closedWaterSprinklerSystems
        ?.isApplicable &&
      this?.inspectionDesign?.sprinklerSystemDesign?.closedWaterSprinklerSystems
        ?.maxWaterNeededAtMainStation?.isApplicable
    ) {
      return this?.inspectionDesign?.sprinklerSystemDesign
        ?.closedWaterSprinklerSystems?.maxWaterNeededAtMainStation
        ?.waterFlowPressure;
    } else if (
      this?.inspectionDesign?.sprinklerSystemDesign?.preactionSystems
        ?.isApplicable &&
      this?.inspectionDesign?.sprinklerSystemDesign?.preactionSystems
        ?.maxWaterNeededAtMainStation?.isApplicable
    ) {
      return this?.inspectionDesign?.sprinklerSystemDesign?.preactionSystems
        ?.maxWaterNeededAtMainStation?.waterFlowPressure;
    } else if (
      this?.inspectionDesign?.sprinklerSystemDesign?.waterFloodingSystems
        ?.isApplicable &&
      this?.inspectionDesign?.sprinklerSystemDesign?.waterFloodingSystems
        ?.maxWaterNeededAtMainStation?.isApplicable
    ) {
      return this?.inspectionDesign?.sprinklerSystemDesign?.waterFloodingSystems
        ?.maxWaterNeededAtMainStation?.waterFlowPressure;
    }

    return { flowRate: undefined, pressure: undefined };
  }

  get isWaterNetworkCharacterizationInspection(): boolean {
    return (
      this?.inspectionType ===
      InspectionTypeEnum.InspectionWaterNetworkCharacterization
    );
  }

  get is1001(): boolean {
    return [
      InspectionTypeEnum.InspectionAirConditioningAndVentilationSystems,
      InspectionTypeEnum.InspectionPressureSystemInStairwellsCompatibilityCheck,
      InspectionTypeEnum.InspectionVentilationSystemAndFireProtection
    ].includes(this?.inspectionType);
  }

  constructor(
    inspection: InspectionDto = {
      summaryNotes: [],
      disclaimer: {},
      standard: '',
      standardExtraText: '',
      fireExtinguishingType: '',
      representativeName: ''
    }
  ) {
    super();
    inspection.disclaimer = inspection.disclaimer ?? {};
    Object.assign(this, inspection);
    this.inspectionDesign = !isEmpty(inspection?.inspectionDesign)
      ? new InspectionDesign(inspection.inspectionDesign)
      : undefined;
    this.inspectionInstallation = !isEmpty(inspection?.inspectionInstallation)
      ? new InspectionInstallation(inspection.inspectionInstallation)
      : undefined;
    this.inspectionWaterNetworkCharacterization = !isEmpty(
      inspection?.inspectionWaterNetworkCharacterization
    )
      ? new WaterNetworkCharacterization(
          inspection.inspectionWaterNetworkCharacterization
        )
      : undefined;
    this.inspectionGasExtinguishingSystem = !isEmpty(
      inspection?.inspectionGasExtinguishingSystem
    )
      ? new InspectionGasExtinguishingSystem(
          inspection.inspectionGasExtinguishingSystem
        )
      : undefined;
    this.inspectionFireTaps = !isEmpty(inspection?.inspectionFireTaps)
      ? new InspectionFireTaps(inspection.inspectionFireTaps)
      : undefined;
    this.inspectionFireExtinguishers = !isEmpty(
      inspection?.inspectionFireExtinguishers
    )
      ? new InspectionFireExtinguishers(inspection.inspectionFireExtinguishers)
      : undefined;
    this.inspectionAerosolShutdown = !isEmpty(
      inspection?.inspectionAerosolShutdown
    )
      ? new InspectionAerosolShutdown(inspection.inspectionAerosolShutdown)
      : undefined;
    this.inspectionFireAlarmSystem = !isEmpty(
      inspection?.inspectionFireAlarmSystem
    )
      ? new InspectionFireAlarmSystem(inspection.inspectionFireAlarmSystem)
      : undefined;
    this.inspectionAirConditioningAndVentilationSystems = !isEmpty(
      inspection?.inspectionAirConditioningAndVentilationSystems
    )
      ? new InspectionAirConditioningAndVentilationSystems(
          inspection.inspectionAirConditioningAndVentilationSystems
        )
      : undefined;
    this.inspectionVentilationSystemAndFireProtection = !isEmpty(
      inspection?.inspectionVentilationSystemAndFireProtection
    )
      ? new InspectionVentilationSystemAndFireProtection(
          inspection.inspectionVentilationSystemAndFireProtection
        )
      : undefined;
    this.inspectionPressureSystemInStairwellsCompatibilityCheck = !isEmpty(
      inspection?.inspectionPressureSystemInStairwellsCompatibilityCheck
    )
      ? new InspectionPressureSystemInStairwellsCompatibilityCheck(
          inspection.inspectionPressureSystemInStairwellsCompatibilityCheck
        )
      : undefined;
    this.inspectionIntegration = !isEmpty(inspection?.inspectionIntegration)
      ? new InspectionIntegration(inspection.inspectionIntegration)
      : undefined;

    this.setInspectionStatus();
  }

  setInspectionStatus(): void {
    this.inspections.forEach((inspection) => inspection?.setInspectionStatus());

    this.setIsPassed();
  }

  removeNonApplicable?(): void {
    this.inspections.forEach((inspection) => inspection?.removeNonApplicable());
  }

  isDefined(): boolean {
    return this.inspections.some((x) => !!x);
  }

  initInspection(): void {
    this.standard = null;

    this.fireExtinguishingType = null;
    this.inspectionDate = null;

    this.inspectionDesign = null;
    this.inspectionInstallation = null;
    this.inspectionWaterNetworkCharacterization = null;
    this.inspectionGasExtinguishingSystem = null;
    this.inspectionFireTaps = null;
    this.inspectionFireExtinguishers = null;
    this.inspectionAerosolShutdown = null;
    this.inspectionFireAlarmSystem = null;
    this.inspectionAirConditioningAndVentilationSystems = null;
    this.inspectionVentilationSystemAndFireProtection = null;
    this.inspectionPressureSystemInStairwellsCompatibilityCheck = null;
    this.inspectionIntegration = null;

    this.summaryNotes = null;
    this.inspectionCount = null;
  }

  setIsPassed(): void {
    this.isPassed = this.inspections
      .filter((inspection) => inspection)
      .every((item) => item.isPassed);
  }

  getActiveInspections(): Inspection[] {
    return this.inspections.filter((inspection) => inspection);
  }

  getActiveInspectionsKeys(): string[] {
    return InspectionTypes.filter((key: InspectionProperty) => this[key]);
  }

  setAllAsApplicable(): void {
    this.inspections.forEach((inspection) => inspection?.setAsApplicable());
  }
}
